import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { call } from '../lib/backbarApi';
import { ConfirmButton, Empty, ErrorNote, Field, Note, PageHead, Section } from '../components/ui';
import { day, planWords, subStatusWords } from '../lib/format';

type SubRow = {
  user_id: string;
  product_id: string | null;
  entitlement: string;
  status: string;
  store: string | null;
  expires_at: string | null;
  updated_at: string | null;
  email: string | null;
  name: string | null;
};
type SubsList = { subscriptions: SubRow[]; counts_by_entitlement: Record<string, number>; ending_within_30_days: SubRow[] };

type GrantRow = {
  id: string;
  user_id: string;
  entitlement: string;
  starts_at: string;
  ends_at: string | null;
  reason: string;
  revoked_at: string | null;
  created_at: string;
  name?: string | null;
  email?: string | null;
};

type Row = {
  key: string;
  user_id: string;
  person: string;
  plan: string;
  source: string;
  when: string;
  status: string;
  tone: 'good' | 'warn' | 'bad' | 'plain';
  grant?: GrantRow;
  endsAt: number | null;
};

const SOON = 30 * 86400_000;

function storeWords(store: string | null): string {
  switch (store) {
    case 'app_store':
    case 'APP_STORE':
    case 'apple':
      return 'App Store';
    case 'play_store':
    case 'PLAY_STORE':
      return 'Google Play';
    case 'seat':
      return 'Included with mentor';
    case 'salon':
      return 'Included with salon';
    default:
      return store ? store.replace(/_/g, ' ') : 'App Store';
  }
}

function grantLive(g: GrantRow, now = Date.now()): boolean {
  return !g.revoked_at && (!g.ends_at || Date.parse(g.ends_at) > now);
}

export function Money() {
  const [params, setParams] = useSearchParams();
  const [subs, setSubs] = useState<SubsList | null>(null);
  const [subsError, setSubsError] = useState<unknown>(null);
  const [grants, setGrants] = useState<GrantRow[] | null>(null);
  const [grantsError, setGrantsError] = useState<unknown>(null);
  const [showForm, setShowForm] = useState(Boolean(params.get('grant')));
  const [filter, setFilter] = useState<'all' | 'store' | 'free' | 'ending'>('all');
  const [done, setDone] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [extending, setExtending] = useState<{ id: string; months: string } | null>(null);

  const load = useCallback(async () => {
    const [s, g] = await Promise.allSettled([
      call<SubsList>('subs.list', {}),
      (async () => {
        const { data, error } = await supabase
          .from('entitlement_grants')
          .select('id, user_id, entitlement, starts_at, ends_at, reason, revoked_at, created_at')
          .order('created_at', { ascending: false })
          .limit(500);
        if (error) throw error;
        const rows = (data ?? []) as GrantRow[];
        const ids = [...new Set(rows.map((r) => r.user_id))];
        const { data: people } = ids.length
          ? await supabase.from('stylists').select('user_id, name, email').in('user_id', ids)
          : { data: [] as { user_id: string; name: string | null; email: string | null }[] };
        const byId = new Map((people ?? []).map((p) => [p.user_id, p]));
        return rows.map((r) => ({ ...r, name: byId.get(r.user_id)?.name ?? null, email: byId.get(r.user_id)?.email ?? null }));
      })(),
    ]);
    if (s.status === 'fulfilled') { setSubs(s.value); setSubsError(null); } else setSubsError(s.reason);
    if (g.status === 'fulfilled') { setGrants(g.value); setGrantsError(null); } else setGrantsError(g.reason);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const now = Date.now();
  const tiles = useMemo(() => {
    const active = (subs?.subscriptions ?? []).filter((s) => s.status === 'active' || s.status === 'grace');
    const academy = active.filter((s) => s.entitlement === 'academy' || s.entitlement === 'academy_plus').length;
    const study = active.filter((s) => s.entitlement === 'study').length;
    const liveGrants = (grants ?? []).filter((g) => grantLive(g, now));
    const endingGrants = liveGrants.filter((g) => g.ends_at && Date.parse(g.ends_at) - now <= SOON).length;
    const endingSubs = subs?.ending_within_30_days.length ?? 0;
    return {
      academy: subs ? academy : null,
      study: subs ? study : null,
      free: grants ? liveGrants.length : null,
      ending: subs && grants ? endingGrants + endingSubs : null,
    };
  }, [subs, grants, now]);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const s of subs?.subscriptions ?? []) {
      const exp = s.expires_at ? Date.parse(s.expires_at) : null;
      const ending = exp != null && exp >= now && exp - now <= SOON && s.status !== 'expired';
      out.push({
        key: `s:${s.user_id}`,
        user_id: s.user_id,
        person: s.name?.trim() || s.email || s.user_id.slice(0, 8),
        plan: planWords(s.entitlement),
        source: storeWords(s.store),
        when: s.expires_at ? `${s.status === 'cancelled' || s.status === 'expired' ? 'Ends' : 'Renews'} ${day(s.expires_at)}` : '—',
        status: subStatusWords(s.status),
        tone: s.status === 'active' ? 'good' : s.status === 'grace' ? 'warn' : s.status === 'expired' ? 'plain' : 'warn',
        endsAt: ending ? exp : null,
      });
    }
    for (const g of grants ?? []) {
      const live = grantLive(g, now);
      const exp = g.ends_at ? Date.parse(g.ends_at) : null;
      const ending = live && exp != null && exp - now <= SOON;
      out.push({
        key: `g:${g.id}`,
        user_id: g.user_id,
        person: g.name?.trim() || g.email || g.user_id.slice(0, 8),
        plan: planWords(g.entitlement),
        source: 'Free access',
        when: g.revoked_at ? `Ended ${day(g.revoked_at)}` : g.ends_at ? `Ends ${day(g.ends_at)}` : 'No end date',
        status: live ? 'Active' : 'Ended',
        tone: live ? 'good' : 'plain',
        grant: g,
        endsAt: ending ? exp : null,
      });
    }
    return out;
  }, [subs, grants, now]);

  const shown = rows.filter((r) => {
    if (filter === 'store') return !r.grant;
    if (filter === 'free') return Boolean(r.grant);
    if (filter === 'ending') return r.endsAt != null;
    return true;
  });

  async function act(label: string, fn: () => Promise<void>) {
    setActionError(null);
    setDone(null);
    try {
      await fn();
      setDone(label);
      await load();
    } catch (err) {
      setActionError(err);
    }
  }

  return (
    <>
      <PageHead
        title="Money"
        sub="Who pays, who has free access, and what ends soon. Charges and refunds themselves happen at Apple."
        right={
          <button type="button" className="lp-btn lp-btn--primary" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Close the form' : 'Give free access'}
          </button>
        }
      />

      {done && <Note tone="good">{done}</Note>}
      <ErrorNote error={actionError} />

      {showForm && (
        <GrantForm
          presetUserId={params.get('grant')}
          presetName={params.get('name')}
          onDone={async (msg) => {
            setShowForm(false);
            setParams({});
            setDone(msg);
            await load();
          }}
        />
      )}

      <div className="lp-cards" style={{ marginTop: 14 }}>
        <Tile n={tiles.academy} title="Active Academy" line="Paying through the App Store" />
        <Tile n={tiles.study} title="Active Study" line="Paying through the App Store" />
        <Tile n={tiles.free} title="Free access" line="Given from here, still running" />
        <Tile n={tiles.ending} title="Ending within 30 days" line="Subscriptions and free access" />
      </div>

      <ErrorNote error={subsError} notConfigured="Subscriptions are not connected yet. The App Store list will appear here once RevenueCat is set up." />
      <ErrorNote error={grantsError} />

      <Section
        title="Everyone with access"
        right={
          <div className="lp-tabs lp-no-print">
            {([['all', 'All'], ['store', 'App Store'], ['free', 'Free access'], ['ending', 'Ending soon']] as const).map(([k, label]) => (
              <button key={k} type="button" className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>{label}</button>
            ))}
          </div>
        }
      >
        <div className="lp-panel" style={{ padding: '6px 24px' }}>
          {!subs && !grants && <Empty>Loading…</Empty>}
          {(subs || grants) && shown.length === 0 && <Empty>Nobody here yet.</Empty>}
          {shown.length > 0 && (
            <div className="lp-tbl-wrap">
              <table className="lp-tbl">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Plan</th>
                    <th>Source</th>
                    <th>Renews or ends</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.key}>
                      <td><Link to={`/people?u=${r.user_id}`} style={{ color: 'var(--moss-deep)', textDecoration: 'underline', textUnderlineOffset: 3 }}>{r.person}</Link></td>
                      <td>{r.plan}</td>
                      <td>{r.source}</td>
                      <td>{r.when}</td>
                      <td><span className={`lp-tag lp-tag--${r.tone}`}>{r.status}</span></td>
                      <td>
                        {r.grant && grantLive(r.grant, now) && (
                          <div className="lp-row">
                            {extending?.id === r.grant.id ? (
                              <>
                                <select className="lp-sel" style={{ width: 'auto', minHeight: 40 }} value={extending.months} onChange={(e) => setExtending({ id: r.grant!.id, months: e.target.value })}>
                                  {[1, 3, 6, 12].map((m) => <option key={m} value={m}>{m} {m === 1 ? 'month' : 'months'}</option>)}
                                </select>
                                <button
                                  type="button"
                                  className="lp-btn lp-btn--small lp-btn--primary"
                                  onClick={() =>
                                    act('Extended.', async () => {
                                      await call('grants.extend', { grant_id: r.grant!.id, months: Number(extending.months) });
                                      setExtending(null);
                                    })
                                  }
                                >
                                  Add
                                </button>
                                <button type="button" className="lp-btn lp-btn--small lp-btn--quiet" onClick={() => setExtending(null)}>Cancel</button>
                              </>
                            ) : (
                              <>
                                <button type="button" className="lp-btn lp-btn--small" onClick={() => setExtending({ id: r.grant!.id, months: '3' })}>Extend</button>
                                <ConfirmButton
                                  label="End now"
                                  sentence={`${r.person} loses free ${r.plan} access right away.`}
                                  yes="Yes, end it"
                                  tone="danger"
                                  onConfirm={() =>
                                    act('Free access ended.', async () => {
                                      await call('grants.revoke', { grant_id: r.grant!.id });
                                    })
                                  }
                                />
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Section>

      <Section title="Refunds and payouts">
        <div className="lp-panel">
          <p style={{ marginTop: 0, fontSize: 17, lineHeight: 1.55 }}>
            <strong>Refunds happen at Apple.</strong> Send the person to{' '}
            <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--moss-deep)', textDecoration: 'underline', textUnderlineOffset: 3 }}>reportaproblem.apple.com</a>.
            To see what Apple has charged someone, open their customer page in{' '}
            <a href="https://app.revenuecat.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--moss-deep)', textDecoration: 'underline', textUnderlineOffset: 3 }}>RevenueCat</a>.
          </p>
          <p style={{ margin: 0, fontSize: 17, lineHeight: 1.55 }}>
            <strong>Apple pays monthly</strong>, about 45 days after the fiscal month closes. The amount and date are in App Store Connect under Payments. The Books page records each month once it lands.
          </p>
        </div>
      </Section>
    </>
  );
}

function Tile({ n, title, line }: { n: number | null; title: string; line: string }) {
  return (
    <div className="lp-card">
      <div className="lp-card-count">{n == null ? '—' : n}</div>
      <div className="lp-card-title">{title}</div>
      <div className="lp-card-line">{line}</div>
    </div>
  );
}

type Hit = { user_id: string | null; name: string | null; email: string | null; role: string | null };

function GrantForm({ presetUserId, presetName, onDone }: { presetUserId: string | null; presetName: string | null; onDone: (msg: string) => Promise<void> }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Hit[]>([]);
  const [searching, setSearching] = useState(false);
  const [person, setPerson] = useState<{ user_id: string; label: string } | null>(
    presetUserId ? { user_id: presetUserId, label: presetName ?? 'the person you picked' } : null,
  );
  const [plan, setPlan] = useState('academy');
  const [months, setMonths] = useState('3');
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  async function search(e: React.FormEvent) {
    e.preventDefault();
    setSearching(true);
    try {
      const d = await call<{ people: Hit[] }>('users.search', { q: q.trim() });
      setHits(d.people.filter((h) => h.user_id));
    } catch (error) {
      setErr(error);
    } finally {
      setSearching(false);
    }
  }

  async function submit() {
    if (!person || !why.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await call('grants.create', {
        user_id: person.user_id,
        entitlement: plan,
        months: months === 'forever' ? null : Number(months),
        reason: why.trim(),
      });
      await onDone(`${person.label} now has free ${planWords(plan)} access${months === 'forever' ? ' with no end date' : ` for ${months} ${months === '1' ? 'month' : 'months'}`}.`);
    } catch (error) {
      setErr(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="lp-panel lp-panel--wash" style={{ marginTop: 14 }}>
      <h2 className="lp-section-title" style={{ fontSize: 26, marginBottom: 12 }}>Give free access</h2>
      <div className="lp-form">
        {!person ? (
          <form onSubmit={search}>
            <Field label="Who">
              <div className="lp-row">
                <input className="lp-inp" style={{ flex: 1, minWidth: 200 }} placeholder="Name or email" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
                <button type="submit" className="lp-btn" disabled={searching}>{searching ? 'Looking…' : 'Search'}</button>
              </div>
            </Field>
            {hits.length > 0 && (
              <ul className="lp-list" style={{ marginBottom: 14 }}>
                {hits.map((h) => (
                  <li key={h.user_id!} style={{ display: 'contents' }}>
                    <button type="button" className="lp-item lp-item--button" onClick={() => setPerson({ user_id: h.user_id!, label: h.name?.trim() || h.email || 'this person' })}>
                      <div className="lp-item-main">
                        <div className="lp-item-title">{h.name?.trim() || h.email}</div>
                        <div className="lp-item-line">{h.email ?? ''}</div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </form>
        ) : (
          <Field label="Who">
            <div className="lp-row">
              <span style={{ fontSize: 18, fontWeight: 600 }}>{person.label}</span>
              <button type="button" className="lp-btn lp-btn--small lp-btn--quiet" onClick={() => setPerson(null)}>Change</button>
            </div>
          </Field>
        )}
        <div className="lp-form-grid">
          <Field label="Plan">
            <select className="lp-sel" value={plan} onChange={(e) => setPlan(e.target.value)}>
              <option value="academy">Academy</option>
              <option value="academy_plus">Academy Plus</option>
              <option value="study">Study</option>
            </select>
          </Field>
          <Field label="How long">
            <select className="lp-sel" value={months} onChange={(e) => setMonths(e.target.value)}>
              <option value="1">1 month</option>
              <option value="3">3 months</option>
              <option value="6">6 months</option>
              <option value="12">12 months</option>
              <option value="forever">No end date</option>
            </select>
          </Field>
        </div>
        <Field label="Why" hint="One line. It stays on the record.">
          <input className="lp-inp" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Workshop attendee, September 2026" />
        </Field>
        <ErrorNote error={err} />
        <div className="lp-row">
          <button type="button" className="lp-btn lp-btn--primary" disabled={!person || !why.trim() || busy} onClick={() => void submit()}>
            {busy ? 'Saving…' : 'Give access'}
          </button>
        </div>
      </div>
    </section>
  );
}
